/**
 * 政策文档统一加载
 * - zh 开头的语言（含 zh-Hant）统一读中文文档（修复此前 zh-Hant 会 import 到不存在的 privacy.zh-Hant.js）
 * - global 版（region !== 'cn'）中文文档中的品牌词替换为 OctoSay、AI 代称统一为 AI；cn 版保留「章摇摇」「小摇」
 * - cn 版年龄口径为 18 周岁、不对未成年人提供服务（算法备案-未成年人保护要求）：terms 注入 __AGE_REQUIREMENT__
 *   并追加《未成年人保护》条款，privacy 注入 __CHILDREN_PRIVACY__；global 版维持 13+ 口径、不含未成年人专项条款
 */
const AGE_REQUIREMENT_CN = '年满18周岁。本应用不对未成年人提供注册使用。'
const AGE_REQUIREMENT_GLOBAL = '年满13岁（或您所在司法管辖区的最低年龄）。'
// cn 版英文协议同样保持 18+ 口径（备案要求与界面语言无关，cn 用户可手动切英文）
const AGE_REQUIREMENT_CN_EN = 'at least 18 years of age. This application does not provide registration or services to minors.'
const AGE_REQUIREMENT_GLOBAL_EN = 'Be at least 13 years of age (or the minimum age in your jurisdiction).'

const MINOR_PROTECTION_SECTION_CN = `
  <h3>未成年人保护</h3>
  <p><strong>本应用不对未成年人提供注册和使用。</strong>章摇摇仅面向年满18周岁的用户提供服务。您在注册时即确认并保证已年满18周岁；如您未满18周岁，请勿注册或使用本应用。</p>
  <p>如我们发现未成年人注册或使用本应用，或接到监护人等有关投诉的，我们有权采取拒绝注册、限制功能、注销账号等处置措施，并依法删除相关未成年人的个人信息。</p>
  <p>我们按照《中华人民共和国未成年人保护法》《未成年人网络保护条例》《互联网信息服务算法推荐管理规定》等法律法规的要求落实未成年人网络保护义务：不设置诱导用户沉迷、过度消费等违反法律法规或者违背伦理道德的算法模型，不向未成年人推送可能影响其身心健康的信息内容。</p>
`

const MINOR_PROTECTION_SECTION_CN_EN = `
  <h3>Protection of Minors</h3>
  <p><strong>This application does not provide registration or services to minors.</strong> 章摇摇 is intended solely for users who have reached the age of 18. By registering, you confirm and warrant that you are at least 18 years old; if you are under 18, please do not register for or use this application.</p>
  <p>If we discover that a minor has registered for or is using this application, or upon complaints from guardians or other relevant parties, we reserve the right to refuse registration, restrict features or delete the account, and to delete the related minors' personal information in accordance with the law.</p>
  <p>In accordance with the Law of the People's Republic of China on the Protection of Minors, the Regulations on the Online Protection of Minors, and the Provisions on the Administration of Algorithmic Recommendation of Internet Information Services, we fulfill our obligations to protect minors online: we do not set up algorithm models that induce addiction, excessive consumption or other violations of laws, regulations or ethical standards, and we do not push information content that may affect the physical and mental health of minors.</p>
`

// 隐私政策「儿童隐私」章节正文：cn 版与用户协议保持一致（18 周岁、不对未成年人提供服务），
// global 版维持 13+ 口径；cn 用户切英文界面时英文隐私政策同样保持 18+ 口径
const CHILDREN_PRIVACY_CN = '本应用不对未成年人提供注册和使用。我们不会故意收集未成年人的个人信息。如我们发现未成年人注册或使用本应用，或收集了相关未成年人的个人信息，我们将采取拒绝注册、限制功能、注销账号等处置措施，并依法删除此类信息。'
const CHILDREN_PRIVACY_GLOBAL = '我们的服务不面向13岁以下儿童。我们不会故意收集13岁以下儿童的个人信息。如果我们发现已收集了13岁以下儿童的个人信息，我们将采取措施删除此类信息。'
const CHILDREN_PRIVACY_CN_EN = 'This application does not provide registration or services to minors. We do not knowingly collect personal information from minors. If we discover that a minor has registered for or is using this application, or that we have collected related personal information, we will take measures such as refusing registration, restricting features or deleting the account, and will delete such information in accordance with the law.'
const CHILDREN_PRIVACY_GLOBAL_EN = 'Our Services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.'

export async function loadPolicyDoc (vm, name) {
  const locale = (vm && vm.$i18n && vm.$i18n.locale) || 'en'
  const isZh = locale.indexOf('zh') === 0
  const region = vm && vm.$config && vm.$config.region
  const isCn = region === 'cn'
  let doc = (await import('@/i18n/policies/' + name + (isZh ? '.zh.js' : '.en.js'))).default
  if (name === 'terms') {
    // cn 版年龄口径为 18 周岁、不对未成年人提供服务（算法备案-未成年人保护要求），并追加《未成年人保护》条款；
    // cn 用户切英文界面时英文协议同样保持该口径，避免勾选框 18+ 与协议 13+ 的裂缝
    if (isCn) {
      doc = doc.replace('__AGE_REQUIREMENT__', isZh ? AGE_REQUIREMENT_CN : AGE_REQUIREMENT_CN_EN)
        .replace('__MINOR_PROTECTION_SECTION__', isZh ? MINOR_PROTECTION_SECTION_CN : MINOR_PROTECTION_SECTION_CN_EN)
    } else {
      doc = doc.replace('__AGE_REQUIREMENT__', isZh ? AGE_REQUIREMENT_GLOBAL : AGE_REQUIREMENT_GLOBAL_EN)
        .replace('__MINOR_PROTECTION_SECTION__', '')
    }
  }
  if (name === 'privacy') {
    // cn 版儿童隐私口径与用户协议保持一致（18 周岁、不对未成年人提供服务），避免 terms 18+ 与 privacy 13+ 的裂缝
    doc = doc.replace('__CHILDREN_PRIVACY__', isCn ? (isZh ? CHILDREN_PRIVACY_CN : CHILDREN_PRIVACY_CN_EN) : (isZh ? CHILDREN_PRIVACY_GLOBAL : CHILDREN_PRIVACY_GLOBAL_EN))
  }
  if (!isCn) {
    doc = doc.replace(/章摇摇/g, 'OctoSay').replace(/小摇/g, 'AI')
  }
  return doc
}



// WEBPACK FOOTER //
// ./src/i18n/policies/loader.js